import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Breadcrumb, Title } from "../../Components/Atoms";

import PATHS from "../../Routes/paths";
import { getDriverOrVehicleStats, getRouteStats, getUserAndVehicleDetails } from "../../APIs";
import { Button, useToast } from "@chakra-ui/react";

import "./styles.css";
import { getISODate, getLocalisedDate, getLocalisedTime, stampToDate } from "../../Library/datetime";
import { ArrowRightIcon } from "../../Assets/images/svgs";
import { getRidingStatus, getStatLabel } from "../../Library/helpers";
import { DATE_RANGES, DEVICE_HWTYPS, RIDING_SCORE_COLORS } from "../../Library/constants";
import { DatePicker } from "../../Components/Molecules";
import { StoreContext } from "../../Library/contexts";
import Loader from "../Loader";

const PastRides = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vId } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const reg = searchParams.get("reg");
  const imei = searchParams.get("imei");
  const isRoute = searchParams.get("route");
  const isTripsSplit = searchParams.get("trips");
  const liveTracking = useRef(searchParams.get("liveTracking") === "true");
  const [showLoading, setShowLoading] = useState(true);
  const [ dailyRides, setDailyRides ] = useState([]);
  const { filters, drivers: driverList, setDrivers, userVehicles } = useContext(StoreContext);
  const prevDateRange = useRef(DATE_RANGES[filters.range]);
  const statsData = useRef(null);
  const dayWiseStatsRef = useRef(null);

  const isUser = useRef(false);
  const isSellerOrDealer = useRef(false);
  const toast = useToast();

  const DAILY_STAT_TYPES = useMemo(() => !isRoute ? [
    { title: "Total Distance", format: "distance", key: "dst"         },
    { title: "Average Speed",  format: "speed"   , key: "avs"         },
    { title: "Top Speed",      format: "speed"   , key: "tps"         },
    { title: "Running Time",   format: "duration", key: "runningTime" },
    { title: "Total Duration", format: "duration", key: "dur"         },
  ] : [
    { title: "No. of Trips",                             key: "trips"                    },
    { title: "Expected Route Dist.", format: "distance", key: "routeDst"                 },
    { title: "Actual Route Dist.",   format: "distance", key: "dst"                      },
    { title: "Deviated Dist.",       format: "distance", key: "deviated", deviated: true },
    { title: "Total Duration",       format: "duration", key: "dur"                      },
  ], [isRoute]);

  const isProDevice = useMemo(() => {
    const vehicle = userVehicles?.find(v => v.idx === +vId);
    return !!vehicle && (!vehicle.hwtyp || vehicle.hwtyp === DEVICE_HWTYPS.Wired);
  }, [userVehicles, vId]);

  const buildDailyRides = (dayWiseStats) => {
    const rides = dayWiseStats.map((ride) => {
      return ({
        cards: DAILY_STAT_TYPES.map((stat, i) => {
          const val = stat.key === "dst" ? (isProDevice ? (ride.dst || 0) : (ride.dst || 0) + (ride.ignOffDst || 0)) : (ride[stat.key] || 0);
          return {
            idx: i,
            title: stat.title,
            label: getStatLabel(val, stat.format),
            deviated: !!stat.deviated && val > 0,
          };
        }),
        meta: ride,
        score: ride?.rideScore,
        status: getRidingStatus(ride?.rideScore),
      })
    });

    setDailyRides(rides);
  };

  useEffect(() => {
    if (dayWiseStatsRef.current) buildDailyRides(dayWiseStatsRef.current);
  }, [isProDevice]);

  const paths = useMemo(() => [
    { idx: 1, name: "Lease", link: PATHS.LEASE },
    { idx: 2, name: "Vehicles", link: PATHS.LEASED_VEHICLES },
    { idx: 3, name: reg || vId, link: [PATHS.LEASED_VEHICLES, PATHS.VEHICLE_STATS].join("/") + `?idx=${vId}` },
    { idx: 3, name: "Past Rides", isCurrentPage: true },
  ], [ reg, vId ]);

  const drivers = useMemo(() => {
    const map = {};
    if (driverList) driverList.forEach(d => map[d.idx] = d.name);
    return map;
  }, [driverList]);

  const userPaths = useMemo(() => {
    let paths = location.pathname.split("/");
    let vehicleId = paths[paths.length - 1];
    let crumbs = [
      { idx: 1, name: isRoute ? "Summary" : "Live Tracking", link: isRoute ? PATHS.VIEW_ROUTES : PATHS.LIVE_TRACKING },
      { idx: 2, name: liveTracking.current ? "Device Details" : `${(reg || vId)} ${isRoute ? "Routes" : ""}` , link: isRoute ? `${PATHS.VIEW_ROUTES}${PATHS.ROUTES_SUMMARY}?reg=${searchParams.get("reg")}&idx=${vId}` : (liveTracking.current ? `${PATHS.LIVE_TRACKING}/${PATHS.SPECIFIC_DEVICE}?imei=${imei}&idx=${vId}&lt=true&showButtons=true&liveTracking=true` : `${PATHS.LIVE_TRACKING}/${PATHS.VEHICLE_STATS}?idx=${vehicleId}&lt=true`) },
      { idx: 3, name: isRoute ? "Day wise split" : "Past Rides", isCurrentPage: true },
    ];

    if (isTripsSplit) {
      let lastCrumb = crumbs[crumbs.length - 1];
      lastCrumb.isCurrentPage = false;
      lastCrumb.link = `${PATHS.VIEW_ROUTES}/${PATHS.PAST_RIDES}/${vId}?route=${isRoute}&reg=${searchParams.get("reg")}`;
      crumbs.push({ idx: 3, name: `${stampToDate(+searchParams.get("ts"))} Trips`, isCurrentPage: true });
    }

    return crumbs;
  }, [imei, liveTracking, location.pathname, reg, vId, isTripsSplit, isRoute]);

  const processStats = (routeStats, dailyStatTypes) => {
    let rides = [];
    if (isTripsSplit) {
      const tsParam = +searchParams.get("ts");
      const dayStart = new Date(tsParam).setHours(0, 0, 0, 0);
      const dayEnd = new Date(tsParam).setHours(23, 59, 59);
      let filteredRoutes = routeStats.filter(route => route.ts >= dayStart && route.ts <= dayEnd);
      let selectedRoute = filteredRoutes[0]?.routes.find(route => route.routeId === +isRoute);
      if (!selectedRoute) return null;
      rides = selectedRoute.tripStats.map(trip => {
        return ({
          cards: dailyStatTypes.map((stat, i) => ({
            idx: i,
            title: stat.title,
            label: getStatLabel((stat.format === "distance" ? (stat.key === "routeDst" ? (selectedRoute[stat.key]) : (trip[stat.key])) : trip[stat.key]) || 0, stat.format, stat.format === "distance" ? 1 : 0),
            deviated: !!stat.deviated && trip[stat.key] > 0,
          })),
          meta: { ...trip, ts: trip.startTs, routeDst: selectedRoute.routeDst },
          score: trip?.rideScore,
          status: getRidingStatus(trip?.rideScore),
        })
      });
    } else {
      rides = routeStats.map(route => {
        let routeToView = route.routes.find(rt => rt.routeId === +isRoute);
        if (!routeToView) return null;

        let driverId = routeToView.tripStats?.[0]?.driverId;
        if (!driverId && location.state?.trips) {
          const match = location.state.trips.find(t => t.startTs >= route.ts && t.startTs <= (route.ts + 86400000));
          if (match) driverId = match.driverId;
        }

        return ({
          cards: dailyStatTypes.map((stat, i) => ({
            idx: i,
            title: stat.title,
            label: getStatLabel((stat.format === "distance" ? routeToView[stat.key] : routeToView[stat.key]) || 0, stat.format, stat.format === "distance" ? 1 : 0),
            deviated: !!stat.deviated && routeToView[stat.key] > 0,
          })),
          meta: { ...routeToView, ts: route.ts, driverId },
          score: routeToView?.rideScore,
          status: getRidingStatus(routeToView?.rideScore),
        })
      });
    }

    setDailyRides(rides.filter(ride => ride));
  }

  const fetchDailyStats = () => {
    let dailyStatTypes = [ ...DAILY_STAT_TYPES ];
    if (isRoute && isTripsSplit) {
      dailyStatTypes.shift();
      dailyStatTypes = dailyStatTypes.concat([{ title: "Avg Speed", format: "speed", key: "avgSpd" }, { title: "Top Speed", format: "speed", key: "topSpd" }]);

      if (statsData.current) return processStats(statsData.current, dailyStatTypes);
    }

    if (isRoute) {
      getRouteStats({ vids: [ +vId ], fromTs: DATE_RANGES[filters.range].from, toTs: DATE_RANGES[filters.range].to }, (err, routeStats) => {
        if (err) {
          console.error(err);
          toast({ isClosable: true, description: err, position: "bottom", variant: "solid", status: "error" });
        } else {
          statsData.current = routeStats;
          processStats(routeStats, dailyStatTypes);
        }
        setShowLoading(false);
      })
    } else {
      getDriverOrVehicleStats(vId, null, DATE_RANGES[filters.range], (error, response) => {
        if (error) {
          console.error(error);
          toast({ isClosable: true, description: error, position: "bottom", variant: "solid", status: "error" });
        } else {
          dayWiseStatsRef.current = response.dayWiseStats;
          buildDailyRides(response.dayWiseStats);
        }
        setShowLoading(false);
      });
    }
  };

  useEffect(() => {
    fetchDailyStats();

    let user = window.localStorage.getItem("user");
    let sellerOrDealer = window.localStorage.getItem("des");
    try {
      user = JSON.parse(user);
      sellerOrDealer = JSON.parse(sellerOrDealer);
    } catch (e) {
      console.error(e);
    }
    isUser.current = user;
    isSellerOrDealer.current = sellerOrDealer;

    if (!driverList || !driverList.length) {
      getUserAndVehicleDetails({ driver: true }, (err, data) => {
        if (!err && data) {
           setDrivers(data.map(d => ({ ...d, name: `${d.fn || ""} ${d.mn || ""} ${d.ln || ""}`.trim() || `Driver ${d.idx}` })));
        }
      });
    }
  }, [ isTripsSplit ]);

  useEffect(() => {
    if (prevDateRange.current !== DATE_RANGES[filters.range]) {
      statsData.current = null;
      fetchDailyStats();
      prevDateRange.current = DATE_RANGES[filters.range];
    }
  }, [filters.range]);

  const handleRide = (ride) => {
    let user = window.localStorage.getItem("user");
    let isSellerOrDealer = window.localStorage.getItem("des");
    try {
      user = JSON.parse(user);
      isSellerOrDealer = JSON.parse(isSellerOrDealer);
    } catch (e) {
      console.error(e);
    }

    if (isRoute) {
      if (!isTripsSplit) return navigate(`${PATHS.VIEW_ROUTES}/${PATHS.PAST_RIDES}/${vId}?route=${isRoute}&reg=${searchParams.get("reg")}&trips=${true}&ts=${ride.ts}`);
      else return navigate([PATHS.VIEW_ROUTES, PATHS.PAST_RIDES, PATHS.RIDE_DETAILS].join("/") + `?reg=${reg}&ts=${ride.ts}&route=${isRoute}&vid=${vId}`, { state: { ride: { ...ride, sdt: ride.startTs/1000, edt: ride.endTs/1000 } } });
    }

    if (user || isSellerOrDealer) {
      navigate([PATHS.LIVE_TRACKING, PATHS.PAST_RIDES, PATHS.RIDE_DETAILS].join("/") + `?reg=${reg}&ts=${ride.ts}${liveTracking.current ? '&liveTracking=true' : ''}&imei=${imei}` , { state: { ride } });
    } else {
      navigate([PATHS.LEASED_VEHICLES, PATHS.PAST_RIDES, PATHS.RIDE_DETAILS].join("/") + `?reg=${reg}&ts=${ride.ts}`, { state: { ride } });
    }
  };

  return showLoading ? <Loader hide={true} /> :
    <div className="panel-main past-rides">
      <header className="past-rides-header">
        <div className="bread-container">
         <Breadcrumb paths={(isUser.current || isSellerOrDealer.current) ? userPaths : paths} />
        </div>
        <DatePicker customMaxDate={getISODate()} onClose={() => fetchDailyStats()} />
      </header>

      {
        dailyRides.length > 0
        ?
        dailyRides.map((ride) => (
          <article key={ride.meta.startTs ? ride.meta.startTs : ride.meta.ts} className="card widget-group">
            <header className="widget-header">
              <Title variant="md">{isTripsSplit ? `${getLocalisedTime(ride.meta.startTs, true)} - ${getLocalisedTime(ride.meta.endTs, true)} ${drivers[ride.meta.driverId] ? `| ${drivers[ride.meta.driverId]}` : ''}` : getLocalisedDate(ride.meta.ts, true)}</Title>
            </header>

            <div className="widget-group keyvals">
              {ride.cards.map((stat) => (
                <main className="widget-main" key={stat.idx}>
                  <header>{stat.title}</header>
                  <article className={stat.deviated ? "highlight-text" : ""}>{stat.label}</article>
                </main>
              ))}
            </div>

            <div className="widget-link">
              <Button variant={"ghost"} rightIcon={<ArrowRightIcon />} onClick={() => handleRide(ride.meta)}>
                {(isRoute && !isTripsSplit) ? "View Trips" : "View Ride"}
              </Button>
            </div>

            {ride && ride.score ? <div className="riding-score">
              <span className="riding-text">Riding Score</span>
              <div className="score-container" style={{ backgroundColor: RIDING_SCORE_COLORS[ride?.status]}}>
                <span className="score">{Number(ride.score) ? Number(ride.score).toFixed() : ride.score}%</span>
              </div>
            </div> : null}
          </article>
        ))
        :
        <div className="no-rides">
          <span>
            No Rides Found for Selected Date Range !!
          </span>
        </div>
      }

    </div>
};


export default PastRides;